import { Observable } from 'rxjs';
import { PaymentType } from 'src/app/modules/invoices/constants/payment-type';
import { TermModel } from '../../modals/settings-statistic/settings-statistic.interface';
import { DataServiceConstants } from './data.constants';

export interface IDataService {
  /**
   * Get global data
   */
  getData(): IParsedDataResponse;

  /**
   * Set global data
   */
  setData(data: any): void;

  /**
   * Get global data from server
   */
  getDataFromServer(): Observable<any>;

  getDataConstants(): DataServiceConstants;
}

interface IPermission {
  name: string;
  items: {
    key: string;
    name: string;
  };
}

export interface IDataResponse {
  data: {
    version: string;
    advertisers: object;
    goals: object;
    offers: object;
    offer_goals: object;
    permissions: IPermission[];
    roles: object;
    tracking_domains: TrackingDomains;
    invoice_currency_codes: string[];
    invoice_statuses: INVOICE_STATUSES[];
    goal_types: object;
    revenue_models: object;
    terms: {
      [key: number]: TermModel;
    };
    affiliate_trackers_placeholder: TermsPlaceholders;
    postback_status: object;
    default_currency: IDefaultCurrency;
    kyc_states: object;
    kyc_types: object;
    kyc_approve_modes: object;
    finance_earning_statuses: Record<string, string>;
    finance_invoice_statuses: Record<string, string>;
    finance_payment_statuses: Record<string, string>;
    payment_systems: { name: keyof typeof PaymentType; currencies: string[] }[];
    payment_systems_list: Record<string, string>;
    currency_list: ICurrency;
    referral_programs: IReferralProgram[];
    payout_models: IPayoutModel;
    default_currencies: Record<string, string>;
    offer_requests_statuses: Record<OFFER_REQUEST_STATUSES, string>;
    isUnpaid: boolean;
    available_offer_currencies: string[];
    offer_currencies_options: ICurrency;
    referralBannerEnabled: boolean;
  };
}

export interface IParsedDataResponse {
  version: string;
  countries: ICountries;
  timezones: ITimezones;
  countriesWithoutAll: ICountries;
  advertisers: object;
  goals: object;
  offers: object;
  offers_full: Record<string, any>;
  offer_goals: Record<string, any>;
  offer_banners: Record<string, Record<string, any>[]>;
  permissions: IPermission[];
  roles: object;
  tracking_domains: TrackingDomains;
  invoice_currency_codes: string[];
  invoice_statuses: string[];
  months: object;
  payout_type: object;
  revenue_type: object;
  conversion_tracking_type: object;
  affiliate_tracker_type: object;
  affiliate_trackers_placeholder: TermsPlaceholders;
  goal_types: object;
  languages: ILanguages;
  all_languages: ILanguages;
  terms: {
    [key: number]: TermModel;
  };
  postback_status: object;
  default_currency: IDefaultCurrency;
  kyc_states: object;
  kyc_types: object;
  kyc_approve_modes: object;
  finance_earning_statuses: Record<string, string>;
  finance_invoice_statuses: Record<string, string>;
  finance_payment_statuses: Record<string, string>;
  user_id: number;
  payment_systems: { name: keyof typeof PaymentType; codes: Record<string, any> }[];
  payment_systems_list: Record<string, string>;
  currency_list: ICurrency;
  payout_models: IPayoutModel;
  referral_programs: IReferralProgram[];
  default_currencies: Record<string, string>;
  available_languages: Record<string, IAvailableLanguage>;
  current_language: string;
  offer_request_statuses: Record<OFFER_REQUEST_STATUSES, string>;
  isUnpaid: boolean;
  available_offer_currencies: string[];
  offer_currencies_options: ICurrency;
  referralBannerEnabled: boolean;
}

export interface IAvailableLanguage {
  code: string;
  name: string;
}

export interface IPayoutModel {
  [key: string]: {
    hint: string;
    label: string;
    name: string;
    placeholder: string;
    short_name: string;
  };
}

export interface ICurrencies {
  [currencies: string]: { code: string; name: string; img: string };
}

export interface ICountries {
  [country: string]: {
    code: string;
    name: string;
    img: string;
    prefix: string;
    currency_code: string;
    regions?: Record<string, string>;
  };
}

export interface ICurrency {
  [key: string]: { code: string; name: string; img: string };
}

export interface ITimezones {
  [timezone: string]: { value: number | string; name: string };
}

export interface ILanguages {
  [language: string]: {
    code: string;
    name: string;
    img: string;
  };
}

export interface IReferralProgram {
  name: string;
  id: number;
}

type TermsPlaceholders = Array<string> | Array<{ [key: string]: string[] }>;

interface IDefaultCurrency {
  currency?: string;
  position?: 'left' | 'right';
}

export enum INVOICE_STATUSES {
  WAITING = 'waiting',
  PENDING = 'pending',
  PAID = 'paid',
  DECLINED = 'declined',
  DRAFT = 'draft',
  APPROVED = 'approved',
}

export enum LANGUAGE_CONSTANT {
  EN = 'en',
  PT = 'pt',
}

export enum OFFER_REQUEST_STATUSES {
  REQUESTED = 'requested',
  DECLINED = 'declined',
  APPROVED = 'approved',
  NOT_REQUESTED = 'not_requested',
}

export enum EARNING_STATUSES {
  APPLIED = 'applied',
  PAID = 'paid',
  DRAFT = 'draft',
}

export interface IOfferUrl {
  url: string;
  id: number;
  default: boolean;
  offer_id: number;
}

export type TrackingDomains = { [id: number]: string };

export enum MetricFormats {
  BOOLEAN = 'boolean',
  FLOAT = 'float',
  NUMBER = 'int',
  MONEY = 'money',
  MONEY_PAYOUT = 'money-payout',
  MONEY_REVENUE = 'money-revenue',
  PERCENTAGE = 'percentage',
  PERCENT = 'percent',
  DATETIME = 'datetime',
}

export enum MetricTypesEnum {
  BASE = 'App\\Modules\\Metrics\\MetricTypes\\BaseMetric',
  NETWORK = 'App\\Modules\\Metrics\\MetricTypes\\NetworkMetric',
}

export interface MetricCardData {
  created_at_timestamp?: string;
  format: string;
  formula: string;
  icon: string;
  id?: number;
  name?: string;
  label?: string;
  model: string;
  filters?: { field: string; operator: string; values: string[] }[];
  aggregate: string;
  target: string;
  description: string;
  metric_type: MetricTypesEnum;
  key: string;
}
